import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { usePostHog } from "@posthog/react";
import {
  createFirestoreRemoteLoginSource,
  type KioskRemoteLoginSource,
} from "../../../api/remote-login";
import { refreshAccessToken } from "../../../shared/auth";
import { appSession } from "../../../shared/lib/storage";
import { writeKioskBinding, type KioskBindingV1 } from "../model/kiosk-binding";
import { recoverKioskIncidentById } from "../model/kiosk-incident";

const APP_ENV =
  import.meta.env.VITE_APP_ENV === "production" ? "production" : "staging";
const LAST_COMMAND_KEY = "kiosk_last_remote_login_command_id";
const RELOAD_FLUSH_DELAY_MS = 600;
const REMOTE_ADMIN = {
  userId: "Remote_Login_By_Admin",
  fullName: "Remote Login By Admin",
} as const;

type RemoteLoginRefresh = (
  input: Parameters<typeof refreshAccessToken>[0],
) => Promise<unknown>;

// Module scope keeps this identity stable across renders. As an inline parameter
// default it would be a new function each render, and since it is a dependency
// of the effect below, the effect would re-evaluate the command on every render
// instead of only when a command or binding actually changes.
const reloadPage = () => window.location.reload();

function readLastCommandId(): string | null {
  try {
    return localStorage.getItem(LAST_COMMAND_KEY);
  } catch {
    return null;
  }
}

function writeLastCommandId(commandId: string): void {
  try {
    localStorage.setItem(LAST_COMMAND_KEY, commandId);
  } catch {
    // Expiry and the presence of a newly stored session remain fallback guards
    // when storage is unavailable.
  }
}

function hasStoredSession(): boolean | null {
  try {
    return appSession.getItem() != null;
  } catch {
    // Fail closed when corrupt storage prevents proving that the app is logged
    // out. The normal kiosk recovery path remains responsible for that state.
    return null;
  }
}

export function KioskRemoteLoginController({
  binding,
  source: sourceOverride,
  refresh = refreshAccessToken,
  reload = reloadPage,
}: {
  binding: KioskBindingV1;
  source?: KioskRemoteLoginSource;
  refresh?: RemoteLoginRefresh;
  reload?: () => void;
}) {
  const posthog = usePostHog();
  const source = useMemo(
    () =>
      sourceOverride ??
      createFirestoreRemoteLoginSource(binding.kioskInstallationId),
    [binding.kioskInstallationId, sourceOverride],
  );
  const command = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const applyingRef = useRef(false);

  useEffect(() => {
    if (!command || applyingRef.current) return;
    if (command.environment !== APP_ENV) return;
    if (command.expiresAtMs <= Date.now()) return;
    if (command.kioskInstallationId !== binding.kioskInstallationId) return;
    if (command.storeKey !== binding.storeKey) return;
    if (command.commandId === readLastCommandId()) return;

    const hasSession = hasStoredSession();
    if (hasSession == null) return;

    const eventProperties = {
      kiosk_installation_id: binding.kioskInstallationId,
      store_key: binding.storeKey,
      zone_id: command.zoneId,
      placement: command.placement,
      environment: APP_ENV,
      incident_id: command.incidentId ?? undefined,
    };
    const capture = (
      event: string,
      properties: Record<string, unknown>,
    ) => {
      try {
        posthog.capture(event, properties);
      } catch {
        // Telemetry must never block authentication recovery.
      }
    };

    if (hasSession) {
      writeLastCommandId(command.commandId);
      capture("kiosk_remote_login_ignored", {
        ...eventProperties,
        reason: "active_session",
      });
      return;
    }

    applyingRef.current = true;
    const nextBinding: KioskBindingV1 = {
      ...binding,
      zoneId: command.zoneId,
      zoneName:
        command.zoneId === binding.zoneId ? binding.zoneName : command.zoneId,
      placement: command.placement,
      configuredAt: new Date().toISOString(),
      configuredBy: REMOTE_ADMIN,
    };

    const clearStagedSession = () => {
      try {
        appSession.removeItem();
      } catch {
        // Best effort when Safari storage is unavailable.
      }
    };
    const fail = (reason: "refresh_failure" | "storage_failure") => {
      applyingRef.current = false;
      capture("kiosk_remote_login_failed", {
        ...eventProperties,
        reason,
      });
    };

    try {
      appSession.setItem({
        access_token: command.accessToken,
        refresh_token: command.refreshToken,
      });
    } catch {
      clearStagedSession();
      fail("storage_failure");
      return;
    }

    void (async () => {
      try {
        await refresh({ storeKey: binding.storeKey, reason: "remote-login" });
      } catch {
        clearStagedSession();
        fail("refresh_failure");
        return;
      }

      try {
        writeKioskBinding(nextBinding);
      } catch {
        clearStagedSession();
        fail("storage_failure");
        return;
      }

      writeLastCommandId(command.commandId);
      const recoveredIncident = command.incidentId
        ? recoverKioskIncidentById(command.incidentId)
        : null;
      if (recoveredIncident) {
        capture("kiosk_incident", {
          lifecycle: "recovered",
          severity: "info",
          incident_id: recoveredIncident.id,
          incident_code: recoveredIncident.code,
          operation: recoveredIncident.operation,
          occurrence_count: recoveredIncident.occurrences,
          outage_duration_ms: recoveredIncident.durationMs,
          kiosk_installation_id: binding.kioskInstallationId,
          store_key: binding.storeKey,
          zone_id: nextBinding.zoneId,
          placement: nextBinding.placement,
          app_version: import.meta.env.VITE_APP_VERSION,
          online: navigator.onLine,
          configured_by_user_id: REMOTE_ADMIN.userId,
          configured_by_user_full_name: REMOTE_ADMIN.fullName,
          recovery_source: "remote_login",
        });
      }
      capture("kiosk_remote_login_applied", {
        ...eventProperties,
        incident_recovered: recoveredIncident != null,
      });
      window.setTimeout(reload, RELOAD_FLUSH_DELAY_MS);
    })();
  }, [binding, command, posthog, refresh, reload]);

  return null;
}
